import { ScopeActivity } from 'app/model/pipeline/scope.activity';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { ActivityAsyncAdapter } from './activity.adapter';
import { toParameterClass, toParameterObject } from 'app/model/adapters/linkedservices/parameter-config.util';
import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { ValueAdapter } from '../value.adapter';
import { ReferenceAdapter } from '../reference.adapter';
import { AzureDataLakeAnalyticsLinkedService } from 'app/model/linkedservice/azure-data-lake-analytics.linkedservice';
import { AzureStorageLinkedService } from 'app/model/linkedservice/azure-storage.linkedservice';
import _ from 'lodash';
import { AzureDataLakeStoreCosmosStructuredStreamFileDataset } from '../../dataset/azure-data-lake-store-cosmos-structured-stream.dataset';
import { Dataset } from 'app/model/dataset/dataset';
import { ParameterValue } from 'app/model/base/parameter-value';
import { Owned } from 'app/model/base/named';
import { ReferenceObjectParameters } from 'app/model/pipeline/reference-object-parameters';
import { convertParametersToModel, fillParameterValuesForResource } from 'app/shared/utils/parameter-converter.util';
import { linkedServiceReferenceWithoutParameterizationRemainer } from '../reference-remainer';
import { AzureDataLakeStoreFileDataset } from '../../dataset/azure-data-lake-store.dataset';
import { AzureDataLakeStoreLinkedService } from 'app/model/linkedservice/azure-data-lake-store.linkedservice';
import { AzureDataLakeStoreCosmosStructuredStreamLinkedService } from '../../linkedservice/azure-data-lake-store-cosmos-structured-stream.linkedservice';
import {
    ActivityConnectionContext,
    IDataLakeAnalyticsScopeActivityContext
} from 'app/interfaces/copilot.pipeline.action.types';
import { type ModelService } from 'app/model/services/model.service';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { findLinkedServiceFromContext } from 'app/shared/utils/update-pipeline-skill.util';
import { LinkedService } from 'shell-models-lib';
import { type IFrameService } from 'app/services/iframe.service';
import { GlobalVariables } from 'app/services/globals';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(ScopeActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        linkedServiceName: linkedServiceReferenceWithoutParameterizationRemainer,
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            scriptFolderPath: '*',
            scriptFileName: '*',
            scriptLinkedService: linkedServiceReferenceWithoutParameterizationRemainer,
            degreeOfParallelism: '*',
            degreeOfParallelismPercent: '*',
            degreeOfParallelismEco: '*',
            degreeOfParallelismPercentEco: '*',
            jobName: '*',
            priority: '*',
            parameters: '*',
            runtimeVersion: '*',
            jobOwner: '*',
            nebulaArguments: '*',
            notifier: '*',
            scopeScriptInclusionSet: '*',
            testIsolation: '*',
            // These last 4 typeProperties are probably obsolete (they appear to have been associated with scope compilation)
            outputDatasets: '*',
            lastCompiledTime: '*',
            outputAdlsLinkedService: linkedServiceReferenceWithoutParameterizationRemainer,
            outputAdlsCosmosStructuredStreamLinkedService: linkedServiceReferenceWithoutParameterizationRemainer
        }
    }
})
export class ScopeActivityAdapter extends ActivityAsyncAdapter<
    ScopeActivity,
    Model.DataLakeAnalyticsScopeActivity,
    IDataLakeAnalyticsScopeActivityContext
> {
    public model(): IModel {
        return Metamodel.modelFor(ScopeActivity);
    }

    public override async toFabricCopilotContext(
        activity: ScopeActivity
    ): Promise<IDataLakeAnalyticsScopeActivityContext> {
        if (activity) {
            const activityContext: IDataLakeAnalyticsScopeActivityContext = await super.toFabricCopilotContext(
                activity
            );
            if (activity.linkedService) {
                const lsAdapter = this.context.asyncAdapterFor<
                    LinkedService,
                    Model.LinkedServiceResource,
                    ActivityConnectionContext
                >(Metamodel.modelOf(activity.linkedService));
                activityContext.Connection = await lsAdapter?.toFabricCopilotContext(activity.linkedService);
                activityContext.ScopeScriptPath = ValueAdapter.toResource(activity, ScopeActivity.FOLDER_PATH, true);
                activityContext.ScopeScriptFilename = ValueAdapter.toResource(activity, ScopeActivity.FILE_NAME, true);
                activityContext.TestProcessIsolation = ValueAdapter.toResource(
                    activity,
                    ScopeActivity.TEST_ISOLATION,
                    true
                );
            }
            return activityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: IDataLakeAnalyticsScopeActivityContext,
        model?: ScopeActivity
    ): Promise<ScopeActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            if (context.Connection) {
                const modelService = await getServiceInstance<ModelService>(this.context.contextData, 'ModelService');
                const iframeService = await getServiceInstance<IFrameService>(
                    this.context.contextData,
                    'IFrameService'
                );
                model.linkedService = (
                    await findLinkedServiceFromContext<AzureDataLakeAnalyticsLinkedService>(context.Connection, {
                        modelService,
                        iframeService
                    })
                ).linkedService;
                if (model.linkedService) {
                    const lsAdapter = Metamodel.isModel(context.Connection.Type)
                        ? this.context.asyncAdapterFor<
                              AzureDataLakeAnalyticsLinkedService,
                              Model.LinkedServiceResource,
                              ActivityConnectionContext
                          >(Metamodel.modelByName(context.Connection.Type))
                        : undefined;
                    await lsAdapter?.fromFabricCopilotContext(context.Connection, model.linkedService);
                }
                ValueAdapter.toModel(model, ScopeActivity.FOLDER_PATH, context.ScopeScriptPath);
                ValueAdapter.toModel(model, ScopeActivity.FILE_NAME, context.ScopeScriptFilename);
                ValueAdapter.toModel(model, ScopeActivity.TEST_ISOLATION, context.TestProcessIsolation);
            }
        }
        return model;
    }

    // -- This is converting our UI information into a Scope Activity for the backend
    protected async _toResource(activity: ScopeActivity): Promise<Model.DataLakeAnalyticsScopeActivity> {
        const modelScopeActivity = super._getCommonActivityProperties(
            activity,
            'DataLakeAnalyticsScope'
        ) as Model.DataLakeAnalyticsScopeActivity;
        const scopeActivity: ScopeActivity = activity as ScopeActivity;

        // The old restriction:
        //    At most only one of the followng 4 parameters should be set.  If we are using "Default" then degreeOfParallelism should be 0
        // The new process
        //    There should be at most one of the non-Eco and one of the 2 non-Eco.  degreeOfParallelism = 0 means use default
        let degreeOfParallelism;
        let degreeOfParallelismPercent;
        let degreeOfParallelismEco;
        let degreeOfParallelismPercentEco;

        // -- This is the existing version
        if (!GlobalVariables.enableScopeMixedMode) {

            const allocationLevel = ValueAdapter.toResource(scopeActivity, ScopeActivity.ALLOCATION_LEVEL);
            const allocationValue = ValueAdapter.toResource(scopeActivity, ScopeActivity.ALLOCATION_VALUE);

            if (allocationValue) {
                if (allocationLevel === 'Token allocation') {
                    degreeOfParallelism =
                        allocationValue.type === 'Expression'
                            ? allocationValue
                            : !isNaN(allocationValue)
                              ? parseInt(allocationValue, 10)
                              : undefined;
                } else if (allocationLevel === 'Percentage allocation') {
                    degreeOfParallelismPercent =
                        allocationValue.type === 'Expression'
                            ? allocationValue
                            : !isNaN(allocationValue)
                              ? parseFloat(allocationValue)
                              : undefined;
                } else if (allocationLevel === 'Eco Token allocation') {
                    degreeOfParallelismEco =
                        allocationValue.type === 'Expression'
                            ? allocationValue
                            : !isNaN(allocationValue)
                              ? parseInt(allocationValue, 10)
                              : undefined;
                } else if (allocationLevel === 'Eco Percentage allocation') {
                    degreeOfParallelismPercentEco =
                        allocationValue.type === 'Expression'
                            ? allocationValue
                            : !isNaN(allocationValue)
                              ? parseFloat(allocationValue)
                              : undefined;
                } else {
                    // -- Should catch unhandled/bad allocationLevel value - throw error?
                    // -- for now we will just set to default
                    degreeOfParallelism = 0;
                }
                // QUESTION: This new method ends up setting all 4 settings - with null set for those that aren't defined. Is there a way to supress the "null" entries?
            } else {
                degreeOfParallelism = 0;
            }
        }
        else {
            // -- This is the new version - 
            const tokenAllocationType  = ValueAdapter.toResource(scopeActivity, ScopeActivity.TOKEN_ALLOCATION_TYPE);
            const normalTokensValue = ValueAdapter.toResource(scopeActivity, ScopeActivity.NORMAL_TOKENS_VALUE);
            const normalTokensPercentValue = ValueAdapter.toResource(scopeActivity, ScopeActivity.NORMAL_TOKENS_PERCENT_VALUE);
            const ecoTokensValue = ValueAdapter.toResource(scopeActivity, ScopeActivity.ECO_TOKENS_VALUE);
            const ecoTokensPercentValue = ValueAdapter.toResource(scopeActivity, ScopeActivity.ECO_TOKENS_PERCENT_VALUE); 

            if (tokenAllocationType !== 'Default') {
                degreeOfParallelism = normalTokensValue?.type === 'Expression'
                            ? normalTokensValue
                            : !isNaN(normalTokensValue)
                              ? parseInt(normalTokensValue, 10)
                              : undefined;
                degreeOfParallelismPercent = normalTokensPercentValue?.type === 'Expression'
                            ? normalTokensPercentValue
                            : !isNaN(normalTokensPercentValue)
                              ? parseFloat(normalTokensPercentValue)
                              : undefined;
                degreeOfParallelismEco = ecoTokensValue?.type === 'Expression'
                            ? ecoTokensValue
                            : !isNaN(ecoTokensValue)
                              ? parseInt(ecoTokensValue, 10)
                              : undefined;
               degreeOfParallelismPercentEco = ecoTokensPercentValue?.type === 'Expression'
                            ? ecoTokensPercentValue
                            : !isNaN(ecoTokensPercentValue)
                              ? parseFloat(ecoTokensPercentValue)
                              : undefined;
     
                // -- if they are all null then we will force degreeOfParallelism to 0
                if ((degreeOfParallelism == null || typeof degreeOfParallelism === 'undefined') &&
                    (degreeOfParallelismPercent == null || typeof degreeOfParallelismPercent === 'undefined') &&
                    (degreeOfParallelismEco == null || typeof degreeOfParallelismEco === 'undefined') &&
                    (degreeOfParallelismPercentEco == null || typeof degreeOfParallelismPercentEco === 'undefined')) {
                        degreeOfParallelism = 0;
                        // -- We might also need to force the others to be completely empty
                        // -- Currently they show as null in typeProperties - not sure how backend will handle this.
                    }
            }
            else {
               degreeOfParallelism = 0; 
            }
        }

        let priority = ValueAdapter.toResource(scopeActivity, ScopeActivity.PRIORITY);
        if (priority !== undefined && priority.type !== 'Expression') {
            priority = priority && !isNaN(priority) ? parseInt(priority, 10) : undefined;
        }

        let parameters = ValueAdapter.toResource(scopeActivity, ScopeActivity.SCOPE_PARAMETERS);
        if (parameters && parameters.type !== 'Expression') {
            parameters = toParameterObject(scopeActivity.parameters);
        }

        modelScopeActivity.typeProperties = {
            scriptFolderPath: ValueAdapter.toResource(scopeActivity, ScopeActivity.FOLDER_PATH),
            scriptFileName: ValueAdapter.toResource(scopeActivity, ScopeActivity.FILE_NAME),
            degreeOfParallelism,
            degreeOfParallelismPercent,
            degreeOfParallelismEco,
            degreeOfParallelismPercentEco,
            jobOwner: ValueAdapter.toResource(scopeActivity, ScopeActivity.JOB_OWNER),
            jobName: ValueAdapter.toResource(scopeActivity, ScopeActivity.JOB_NAME),
            notifier: ValueAdapter.toResource(scopeActivity, ScopeActivity.EMAIL_RECIPIENTS),
            scopeScriptInclusionSet: ValueAdapter.toResource(scopeActivity, ScopeActivity.SCOPE_SCRIPT_INCLUSION_SET),
            priority,
            runtimeVersion: ValueAdapter.toResource(scopeActivity, ScopeActivity.RUNTIME_VERSION),
            scriptLinkedService:
                scopeActivity.scriptLinkedService != null
                    ? this._toLinkedServiceReferenceModel(scopeActivity.scriptLinkedService)
                    : undefined,
            parameters,
            nebulaArguments: ValueAdapter.toResource(scopeActivity, ScopeActivity.NEBULA_ARGUMENTS),
            testIsolation: ValueAdapter.toResource(scopeActivity, ScopeActivity.TEST_ISOLATION),
            // -- these last ones are probably obsolete (should be removed in future PR)
            lastCompiledTime: scopeActivity.lastCompiledTime,
            outputAdlsLinkedService:
                scopeActivity.outputAdlsLinkedService != null
                    ? this._toLinkedServiceReferenceModel(scopeActivity.outputAdlsLinkedService)
                    : undefined,
            outputAdlsCosmosStructuredStreamLinkedService:
                scopeActivity.outputAdlsCosmosStructuredStreamLinkedService != null
                    ? this._toLinkedServiceReferenceModel(scopeActivity.outputAdlsCosmosStructuredStreamLinkedService)
                    : undefined
        };

        // -- this is probably obsolete
        if (scopeActivity.outputDatasets && scopeActivity.outputDatasets.length > 0) {
            this.populateDatasetReferences(scopeActivity, modelScopeActivity.typeProperties);
        }

        if (scopeActivity.linkedService) {
            modelScopeActivity.linkedServiceName = this._toLinkedServiceReferenceModel(scopeActivity.linkedService);
        }

        if (scopeActivity.raw) {
            const parsed = JSON.parse(scopeActivity.raw);
            // Overlay this on scope activity
            _.merge(modelScopeActivity, parsed);
        }

        return modelScopeActivity;
    }

    // -- This converts an existing Scope activity definition into the data to be displayed in the UI
    protected async _toModel(modelActivity: Model.DataLakeAnalyticsScopeActivity): Promise<ScopeActivity> {
        const activity: ScopeActivity = new ScopeActivity(modelActivity.name);
        const modelScopeActivity = modelActivity as Model.DataLakeAnalyticsScopeActivity;
        this._updatedCommonProperties(modelScopeActivity, activity);

        if (modelScopeActivity.typeProperties) {
            ValueAdapter.toModel(
                activity,
                ScopeActivity.FOLDER_PATH,
                modelScopeActivity.typeProperties.scriptFolderPath
            );
            ValueAdapter.toModel(activity, ScopeActivity.FILE_NAME, modelScopeActivity.typeProperties.scriptFileName);

            // -- The old method
            if (!GlobalVariables.enableScopeMixedMode) {
                if (modelScopeActivity.typeProperties.degreeOfParallelism) {
                    if (modelScopeActivity.typeProperties.degreeOfParallelism.type === 'Expression') {
                        ValueAdapter.toModel(
                            activity,
                            ScopeActivity.ALLOCATION_VALUE,
                            modelScopeActivity.typeProperties.degreeOfParallelism
                        );
                    } else {
                        activity.allocationValue = JSON.stringify(modelScopeActivity.typeProperties.degreeOfParallelism);
                    }
                    activity.allocationLevel = activity.allocationValue === '0' ? 'Default' : 'Token allocation';
                } else if (modelScopeActivity.typeProperties.degreeOfParallelismPercent) {
                    if (modelScopeActivity.typeProperties.degreeOfParallelismPercent.type === 'Expression') {
                        ValueAdapter.toModel(
                            activity,
                            ScopeActivity.ALLOCATION_VALUE,
                            modelScopeActivity.typeProperties.degreeOfParallelismPercent
                        );
                    } else {
                        activity.allocationValue = JSON.stringify(
                            modelScopeActivity.typeProperties.degreeOfParallelismPercent
                        );
                    }
                    activity.allocationLevel = 'Percentage allocation';
                } else if (modelScopeActivity.typeProperties.degreeOfParallelismEco) {
                    if (modelScopeActivity.typeProperties.degreeOfParallelismEco.type === 'Expression') {
                        ValueAdapter.toModel(
                            activity,
                            ScopeActivity.ALLOCATION_VALUE,
                            modelScopeActivity.typeProperties.degreeOfParallelismEco
                        );
                    } else {
                        activity.allocationValue = JSON.stringify(modelScopeActivity.typeProperties.degreeOfParallelismEco);
                    }
                    activity.allocationLevel = 'Eco Token allocation';
                } else if (modelScopeActivity.typeProperties.degreeOfParallelismPercentEco) {
                    if (modelScopeActivity.typeProperties.degreeOfParallelismPercentEco.type === 'Expression') {
                        ValueAdapter.toModel(
                            activity,
                            ScopeActivity.ALLOCATION_VALUE,
                            modelScopeActivity.typeProperties.degreeOfParallelismPercentEco
                        );
                    } else {
                        activity.allocationValue = JSON.stringify(
                            modelScopeActivity.typeProperties.degreeOfParallelismPercentEco
                        );
                    }
                    activity.allocationLevel = 'Eco Percentage allocation';
                } else {
                    // -- Force it to Default if nothing else matches
                    activity.allocationValue = null;
                    activity.allocationLevel = 'Default';
                }
            }
            else {
                // -- The new method - if the typeProperty exists - then extract the value and populate the field
                activity.normalTokensValue = null;
                activity.normalTokensPercentValue = null;
                activity.ecoTokensValue = null;
                activity.ecoTokensPercentValue = null;

                if (modelScopeActivity.typeProperties.degreeOfParallelism) {
                    if (modelScopeActivity.typeProperties.degreeOfParallelism.type === 'Expression') {
                            ValueAdapter.toModel(
                                activity,
                                ScopeActivity.NORMAL_TOKENS_VALUE,
                                modelScopeActivity.typeProperties.degreeOfParallelism
                            );
                    } else {
                        activity.normalTokensValue = JSON.stringify(modelScopeActivity.typeProperties.degreeOfParallelism);
                    }
                }

                if (modelScopeActivity.typeProperties.degreeOfParallelismPercent) {
                    if (modelScopeActivity.typeProperties.degreeOfParallelismPercent.type === 'Expression') {
                        ValueAdapter.toModel(
                            activity,
                            ScopeActivity.NORMAL_TOKENS_PERCENT_VALUE,
                            modelScopeActivity.typeProperties.degreeOfParallelismPercent
                        );
                    } else {
                        activity.normalTokensPercentValue = JSON.stringify(modelScopeActivity.typeProperties.degreeOfParallelismPercent);
                    }
                }

                if (modelScopeActivity.typeProperties.degreeOfParallelismEco) {
                    if (modelScopeActivity.typeProperties.degreeOfParallelismEco.type === 'Expression') {
                            ValueAdapter.toModel(
                                activity,
                                ScopeActivity.ECO_TOKENS_VALUE,
                                modelScopeActivity.typeProperties.degreeOfParallelismEco
                            );
                    } else {
                        activity.ecoTokensValue = JSON.stringify(modelScopeActivity.typeProperties.degreeOfParallelismEco);
                    }
                }

                if (modelScopeActivity.typeProperties.degreeOfParallelismPercentEco) {
                    if (modelScopeActivity.typeProperties.degreeOfParallelismPercentEco.type === 'Expression') {
                        ValueAdapter.toModel(
                            activity,
                            ScopeActivity.ECO_TOKENS_PERCENT_VALUE,
                            modelScopeActivity.typeProperties.degreeOfParallelismPercentEco
                        );
                    } else {
                        activity.ecoTokensPercentValue = JSON.stringify(modelScopeActivity.typeProperties.degreeOfParallelismPercentEco);
                    }
                }
                // -- A token value of 0 indicates that default should be used
                if (activity.normalTokensValue == '0') { 
                    activity.tokenAllocationType = 'Default';
                    activity.normalTokensValue = null;
                }

                if (activity.normalTokensValue == null && activity.normalTokensPercentValue == null &&
                      activity.ecoTokensValue == null && activity.ecoTokensPercentValue == null) {
                    activity.tokenAllocationType = 'Default';
                }
                else {
                    activity.tokenAllocationType =  'Custom';
                }
            }           
            // -- END OF NEW METHOD

            if (modelScopeActivity.typeProperties.priority) {
                if (modelScopeActivity.typeProperties.priority.type === 'Expression') {
                    ValueAdapter.toModel(activity, ScopeActivity.PRIORITY, modelScopeActivity.typeProperties.priority);
                } else {
                    activity.priority = JSON.stringify(modelScopeActivity.typeProperties.priority);
                }
            }

            ValueAdapter.toModel(
                activity,
                ScopeActivity.RUNTIME_VERSION,
                modelScopeActivity.typeProperties.runtimeVersion
            );
            ValueAdapter.toModel(
                activity,
                ScopeActivity.NEBULA_ARGUMENTS,
                modelScopeActivity.typeProperties.nebulaArguments
            );

            ValueAdapter.toModel(activity, ScopeActivity.JOB_NAME, modelScopeActivity.typeProperties.jobName);
            ValueAdapter.toModel(activity, ScopeActivity.JOB_OWNER, modelScopeActivity.typeProperties.jobOwner);
            ValueAdapter.toModel(activity, ScopeActivity.EMAIL_RECIPIENTS, modelScopeActivity.typeProperties.notifier);
            ValueAdapter.toModel(
                activity,
                ScopeActivity.SCOPE_SCRIPT_INCLUSION_SET,
                modelScopeActivity.typeProperties.scopeScriptInclusionSet
            );

            if (modelScopeActivity.typeProperties.scriptLinkedService != null) {
                activity.scriptLinkedService = ReferenceAdapter.toProxyModel<AzureStorageLinkedService>(
                    Metamodel.modelFor(AzureStorageLinkedService),
                    modelScopeActivity.typeProperties.scriptLinkedService,
                    this.context
                );
            }
            if (
                modelScopeActivity.typeProperties.parameters &&
                modelScopeActivity.typeProperties.parameters['type'] &&
                modelScopeActivity.typeProperties.parameters['type'] === 'Expression'
            ) {
                ValueAdapter.toModel(
                    activity,
                    ScopeActivity.SCOPE_PARAMETERS,
                    modelScopeActivity.typeProperties.parameters
                );
            } else {
                activity.parameters = toParameterClass(modelScopeActivity.typeProperties.parameters, activity);
            }
            if (
                (modelScopeActivity.typeProperties.outputAdlsDatasets &&
                    modelScopeActivity.typeProperties.outputAdlsDatasets.length > 0) ||
                (modelScopeActivity.typeProperties.outputAdlsCosmosStructuredStreamDatasets &&
                    modelScopeActivity.typeProperties.outputAdlsCosmosStructuredStreamDatasets.length > 0)
            ) {
                activity.outputDatasets = [];
                activity.outputDatasetsParameters = [];
                this.populateOutputDatasets(
                    activity,
                    modelScopeActivity.typeProperties.outputAdlsCosmosStructuredStreamDatasets,
                    true
                );
                this.populateOutputDatasets(activity, modelScopeActivity.typeProperties.outputAdlsDatasets, false);
            }

            ValueAdapter.toModel(
                activity,
                ScopeActivity.TEST_ISOLATION,
                modelScopeActivity.typeProperties.testIsolation
            );

            // -- These last 2 are almost certainly obsolete (deprecated compilation capability)
            activity.lastCompiledTime = modelScopeActivity.typeProperties.lastCompiledTime;
            if (modelScopeActivity.typeProperties.outputAdlsLinkedService != null) {
                activity.outputAdlsLinkedService = ReferenceAdapter.toProxyModel<AzureDataLakeStoreLinkedService>(
                    Metamodel.modelFor(AzureDataLakeStoreLinkedService),
                    modelScopeActivity.typeProperties.outputAdlsLinkedService,
                    this.context
                );
            }
            if (modelScopeActivity.typeProperties.outputAdlsCosmosStructuredStreamLinkedService != null) {
                activity.outputAdlsCosmosStructuredStreamLinkedService =
                    ReferenceAdapter.toProxyModel<AzureDataLakeStoreCosmosStructuredStreamLinkedService>(
                        Metamodel.modelFor(AzureDataLakeStoreCosmosStructuredStreamLinkedService),
                        modelScopeActivity.typeProperties.outputAdlsCosmosStructuredStreamLinkedService,
                        this.context
                    );
            }
        }

        if (modelScopeActivity.linkedServiceName != null) {
            activity.linkedService = ReferenceAdapter.toProxyModel<AzureDataLakeAnalyticsLinkedService>(
                Metamodel.modelFor(AzureDataLakeAnalyticsLinkedService),
                modelScopeActivity.linkedServiceName,
                this.context
            );
        }

        activity.raw = JSON.stringify(
            await ValueAdapter.remainder(this.context, modelScopeActivity, ScopeActivity.MODEL)
        );
        return activity;
    }

    private populateDatasetReferences(
        activity: ScopeActivity,
        typeProperties: Model.DataLakeAnalyticsScopeActivityTypeProperties
    ): void {
        typeProperties.outputAdlsDatasets = [];
        typeProperties.outputAdlsCosmosStructuredStreamDatasets = [];
        activity.outputDatasets.forEach((outputDataset, idx) => {
            if (outputDataset instanceof Dataset) {
                const datasetReference = ReferenceAdapter.toResource(
                    outputDataset,
                    this.context
                ) as Model.DatasetReference;
                const datasetParameters: ParameterValue[] = [];
                outputDataset.parameters?.forEach((param) => {
                    if (param.name === 'inputDateTime') {
                        const paramReference = Owned.create<ParameterValue>(ParameterValue.MODEL, activity);
                        paramReference.value = `@pipeline().parameters.${activity.dateTimeParamName}`;
                        paramReference.valueType = 'Expression';
                        paramReference.parameter = param;
                        datasetParameters.push(paramReference);
                    }
                });
                activity.outputDatasetsParameters[idx].parameters
                    ?.filter((param) => param.parameter.name !== 'inputDateTime')
                    .forEach((param) => datasetParameters.push(param));
                datasetReference.parameters = fillParameterValuesForResource(
                    datasetParameters,
                    this.context,
                    this.ensureLogger()
                );
                if (outputDataset instanceof AzureDataLakeStoreCosmosStructuredStreamFileDataset) {
                    typeProperties.outputAdlsCosmosStructuredStreamDatasets.push(datasetReference);
                } else {
                    typeProperties.outputAdlsDatasets.push(datasetReference);
                }
            }
        });
    }

    private populateOutputDatasets(
        activity: ScopeActivity,
        datasetReferences: Model.DatasetReference[],
        isCosmosStructuredStream: boolean
    ): void {
        if (datasetReferences) {
            datasetReferences.forEach((datasetReference) => {
                const refDataset = isCosmosStructuredStream
                    ? ReferenceAdapter.toProxyModel<AzureDataLakeStoreCosmosStructuredStreamFileDataset>(
                          Metamodel.modelFor(AzureDataLakeStoreCosmosStructuredStreamFileDataset),
                          datasetReference,
                          this.context
                      )
                    : ReferenceAdapter.toProxyModel<AzureDataLakeStoreFileDataset>(
                          Metamodel.modelFor(AzureDataLakeStoreFileDataset),
                          datasetReference,
                          this.context
                      );
                const paramValues: ParameterValue[] = [];

                const refParam = Owned.create<ReferenceObjectParameters>(ReferenceObjectParameters.MODEL, activity);
                if (datasetReference.parameters) {
                    convertParametersToModel(
                        refParam,
                        !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                        refDataset,
                        paramValues,
                        datasetReference.parameters
                    );
                    refParam.parameters = paramValues;
                }
                activity.outputDatasetsParameters.push(refParam);
                activity.outputDatasets.push(refDataset);
            });
        }
    }
}
